// f64/complex.wgsl — double-double (DD) precision complex numbers, mirroring
// helpers/complex.wgsl's Complex/cAdd/cMul/cAbs2 API but at ~48 bits of
// mantissa instead of f32's 24 (see f64/dekker.wgsl's header for what a DD
// pair is and why it beats f32 without reaching true f64).
//
// Requires, concatenated first in this order: f64/dekker.wgsl (DD struct),
// f64/utils/add.wgsl (ddAddProtected, and fastTwoSumProtected needed
// transitively by multiply.wgsl's ddMulProtected), f64/utils/multiply.wgsl
// (ddMulProtected), f64/utils/subtract.wgsl (ddSubProtected) — see
// src/util/pipeline.mjs's F64_MODULES list, which is the source of truth
// for this order.
//
// Every op below threads a `threadSlot` through to the underlying
// *Protected DD primitives, and inherits their workgroupBarrier()
// requirement: every invocation in the workgroup must call it the same
// number of times, in the same order (see dekker.wgsl's header). A caller
// with a divergent per-pixel loop — an escape-time fractal — can't `break`
// early because of this; see fractals/f64/mandelbrot.wgsl for how it stays
// workgroup-uniform instead.

struct ComplexDD { re: DD, im: DD }

fn cdAdd(a: ComplexDD, b: ComplexDD, threadSlot: u32) -> ComplexDD {
  return ComplexDD(
    ddAddProtected(a.re, b.re, threadSlot),
    ddAddProtected(a.im, b.im, threadSlot),
  );
}

fn cdMul(a: ComplexDD, b: ComplexDD, threadSlot: u32) -> ComplexDD {
  let rr = ddMulProtected(a.re, b.re, threadSlot);
  let ii = ddMulProtected(a.im, b.im, threadSlot);
  let ri = ddMulProtected(a.re, b.im, threadSlot);
  let ir = ddMulProtected(a.im, b.re, threadSlot);
  return ComplexDD(
    ddSubProtected(rr, ii, threadSlot),
    ddAddProtected(ri, ir, threadSlot),
  );
}

// |a|^2 as a DD, for comparing against an escape-radius-squared constant via
// ddGreater — kept in DD rather than collapsed to f32 so the comparison
// doesn't throw away the precision the rest of the loop worked to keep.
fn cdAbs2(a: ComplexDD, threadSlot: u32) -> DD {
  let rr = ddMulProtected(a.re, a.re, threadSlot);
  let ii = ddMulProtected(a.im, a.im, threadSlot);
  return ddAddProtected(rr, ii, threadSlot);
}
